import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Filter, Search, Plus, Trash2, Edit2, Save, X, 
  Check, FileSpreadsheet, RefreshCw, Settings, ChevronDown,
  AlertCircle, CheckCircle, BarChart2, Download, BookOpen, Sparkles, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Standard Australian bank formats
const BANK_FORMATS = [
  { value: 'generic', label: 'Generic CSV' },
  { value: 'commbank', label: 'Commonwealth Bank' },
  { value: 'anz', label: 'ANZ' },
  { value: 'westpac', label: 'Westpac' },
  { value: 'nab', label: 'NAB' }
];

function BankTransactionsTab() {
  // State
  const [transactions, setTransactions] = useState([]);
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('transactions'); // transactions, rules, import, bas, suggestions
  
  // Filter state
  const [filters, setFilters] = useState({
    category: '',
    is_reviewed: '',
    account_name: '',
    search: ''
  });
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [bankFormat, setBankFormat] = useState('generic');
  const [accountName, setAccountName] = useState('Main Account');
  const [importing, setImporting] = useState(false);
  
  // Rule form state
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    name: '',
    conditions: [{ field: 'description', operator: 'contains', value: '' }],
    category: '',
    subcategory: '',
    tax_code: 'GST',
    priority: 0
  });
  
  // BAS date range
  const [basStartDate, setBasStartDate] = useState('');
  const [basEndDate, setBasEndDate] = useState('');
  const [basReport, setBasReport] = useState(null);
  
  // Quick Rules suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [transRes, rulesRes, catRes] = await Promise.all([
        axios.get(`${API}/transactions/`, { 
          params: { limit: 500 },
          withCredentials: true 
        }),
        axios.get(`${API}/transactions/rules`, { withCredentials: true }),
        axios.get(`${API}/transactions/categories`, { withCredentials: true })
      ]);
      setTransactions(transRes.data.transactions || []);
      setRules(rulesRes.data.rules || []);
      setCategories(catRes.data.categories || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load bank transactions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Set default BAS date range to current quarter
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const startMonth = quarter * 3;
    setBasStartDate(new Date(now.getFullYear(), startMonth, 1).toISOString().split('T')[0]);
    setBasEndDate(new Date(now.getFullYear(), startMonth + 3, 0).toISOString().split('T')[0]);
  }, [fetchData]);

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (filters.category && t.category !== filters.category) return false;
    if (filters.is_reviewed === 'true' && !t.is_reviewed) return false;
    if (filters.is_reviewed === 'false' && t.is_reviewed) return false;
    if (filters.account_name && t.account_name !== filters.account_name) return false;
    if (filters.search && !t.description.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  // Fetch Quick Rules suggestions
  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await axios.get(`${API}/transactions/suggest-rules`, { withCredentials: true });
      setSuggestions(response.data.suggestions || []);
      if (response.data.suggestions?.length === 0) {
        toast.info('No patterns found. Import more transactions to get suggestions.');
      }
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      toast.error('Failed to get rule suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Accept a suggested rule
  const handleAcceptSuggestion = async (suggestion) => {
    try {
      await axios.post(`${API}/transactions/accept-suggestion`, suggestion, { withCredentials: true });
      toast.success(`Rule "${suggestion.suggested_rule.name}" created!`);
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.pattern !== suggestion.pattern));
      fetchData();
    } catch (error) {
      toast.error('Failed to create rule');
    }
  };

  // Accept all suggestions
  const handleAcceptAll = async () => {
    for (const suggestion of suggestions) {
      try {
        await axios.post(`${API}/transactions/accept-suggestion`, suggestion, { withCredentials: true });
      } catch (error) {
        console.error('Failed to accept suggestion:', error);
      }
    }
    toast.success(`Created ${suggestions.length} rules!`);
    setSuggestions([]);
    fetchData();
  };

  // Handle CSV import
  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    try {
      const response = await axios.post(
        `${API}/transactions/import/csv?bank_format=${bankFormat}&account_name=${encodeURIComponent(accountName)}`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      toast.success(`Imported ${response.data.imported} transactions (${response.data.auto_categorized} auto-categorized)`);
      setImportFile(null);
      setActiveView('transactions');
      fetchData();
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  // Handle rule save
  const handleSaveRule = async (e) => {
    e.preventDefault();
    if (!ruleForm.name.trim() || !ruleForm.category) {
      toast.error('Name and category are required');
      return;
    }
    
    try {
      if (editingRule) {
        await axios.put(`${API}/transactions/rules/${editingRule}`, ruleForm, { withCredentials: true });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API}/transactions/rules`, ruleForm, { withCredentials: true });
        toast.success('Rule created');
      }
      setShowRuleForm(false);
      setEditingRule(null);
      resetRuleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    }
  };

  // Handle rule delete
  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this rule?')) return;
    
    try {
      await axios.delete(`${API}/transactions/rules/${ruleId}`, { withCredentials: true });
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  // Edit rule
  const handleEditRule = (rule) => {
    setRuleForm({
      name: rule.name,
      conditions: rule.conditions,
      category: rule.category,
      subcategory: rule.subcategory || '',
      tax_code: rule.tax_code || 'GST',
      priority: rule.priority || 0
    });
    setEditingRule(rule.rule_id);
    setShowRuleForm(true);
  };

  // Reset rule form
  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      conditions: [{ field: 'description', operator: 'contains', value: '' }],
      category: '',
      subcategory: '',
      tax_code: 'GST',
      priority: 0
    });
  };

  // Add condition to rule
  const addCondition = () => {
    setRuleForm({
      ...ruleForm,
      conditions: [...ruleForm.conditions, { field: 'description', operator: 'contains', value: '' }]
    });
  };

  // Remove condition from rule
  const removeCondition = (index) => {
    setRuleForm({
      ...ruleForm,
      conditions: ruleForm.conditions.filter((_, i) => i !== index)
    });
  };

  // Update transaction category
  const handleUpdateTransaction = async (transactionId, updates) => {
    try {
      await axios.put(`${API}/transactions/${transactionId}`, updates, { withCredentials: true });
      toast.success('Transaction updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  // Apply rules to uncategorized
  const handleApplyRules = async () => {
    try {
      const response = await axios.post(`${API}/transactions/apply-rules`, {}, { withCredentials: true });
      toast.success(`Categorized ${response.data.categorized} transactions`);
      fetchData();
    } catch (error) {
      toast.error('Failed to apply rules');
    }
  };

  // Get BAS summary
  const handleGetBAS = async () => {
    if (!basStartDate || !basEndDate) {
      toast.error('Please select date range');
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/transactions/bas-summary?start_date=${basStartDate}&end_date=${basEndDate}`,
        { withCredentials: true }
      );
      setBasReport(response.data);
      toast.success('BAS summary generated');
    } catch (error) {
      toast.error('Failed to generate BAS summary');
    }
  };

  // Get unique accounts
  const accounts = [...new Set(transactions.map(t => t.account_name))];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bank-transactions-tab">
      {/* Header with Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Total Transactions</div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">{transactions.length}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Categorized</div>
          <div className="text-2xl font-bold text-emerald-600">{transactions.filter(t => t.category).length}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Needs Review</div>
          <div className="text-2xl font-bold text-amber-600">{transactions.filter(t => !t.category).length}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-xl p-4 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-500 dark:text-slate-400">Active Rules</div>
          <div className="text-2xl font-bold text-indigo-600">{rules.filter(r => r.is_active).length}</div>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700 pb-2 overflow-x-auto">
        {[
          { id: 'transactions', label: 'Transactions', icon: FileSpreadsheet },
          { id: 'import', label: 'Import CSV', icon: Upload },
          { id: 'rules', label: 'Rules', icon: Settings },
          { id: 'suggestions', label: 'Quick Rules', icon: Sparkles },
          { id: 'bas', label: 'BAS Export', icon: BookOpen }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => {
              setActiveView(tab.id);
              if (tab.id === 'suggestions' && suggestions.length === 0) {
                fetchSuggestions();
              }
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeView === tab.id
                ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
            }`}
            data-testid={`bank-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.id === 'suggestions' && suggestions.length > 0 && (
              <span className="px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">{suggestions.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Transactions View */}
      {activeView === 'transactions' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
                data-testid="bank-search-input"
              />
            </div>
            
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
              data-testid="bank-filter-category"
            >
              <option value="">All Categories</option>
              {Object.entries(categories).map(([key, cat]) => (
                <option key={key} value={key}>{cat.name}</option>
              ))}
            </select>
            
            <select
              value={filters.is_reviewed}
              onChange={(e) => setFilters({ ...filters, is_reviewed: e.target.value })}
              className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
            >
              <option value="">All Status</option>
              <option value="true">Reviewed</option>
              <option value="false">Needs Review</option>
            </select>
            
            {accounts.length > 1 && (
              <select
                value={filters.account_name}
                onChange={(e) => setFilters({ ...filters, account_name: e.target.value })}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm"
              >
                <option value="">All Accounts</option>
                {accounts.map(acc => (
                  <option key={acc} value={acc}>{acc}</option>
                ))}
              </select>
            )}
            
            <button
              onClick={handleApplyRules}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
              data-testid="apply-rules-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Apply Rules
            </button>
          </div>

          {/* Transactions Table */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {filteredTransactions.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No transactions found. Import a bank statement to get started.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Date</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Description</th>
                      <th className="text-right px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Amount</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Category</th>
                      <th className="text-left px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Tax Code</th>
                      <th className="text-center px-4 py-3 font-medium text-slate-600 dark:text-slate-300">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                    {filteredTransactions.slice(0, 100).map(trans => (
                      <tr key={trans.transaction_id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{trans.date}</td>
                        <td className="px-4 py-3 text-slate-900 dark:text-white max-w-xs truncate">{trans.description}</td>
                        <td className={`px-4 py-3 text-right font-medium ${trans.amount >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {trans.amount >= 0 ? '+' : ''}{trans.amount.toFixed(2)}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={trans.category || ''}
                            onChange={(e) => handleUpdateTransaction(trans.transaction_id, { category: e.target.value })}
                            className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                          >
                            <option value="">Select...</option>
                            {Object.entries(categories).map(([key, cat]) => (
                              <option key={key} value={key}>{cat.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={trans.tax_code || 'GST'}
                            onChange={(e) => handleUpdateTransaction(trans.transaction_id, { tax_code: e.target.value })}
                            className="px-2 py-1 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                          >
                            <option value="GST">GST</option>
                            <option value="GST-Free">GST-Free</option>
                            <option value="Input Taxed">Input Taxed</option>
                            <option value="N/A">N/A</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleUpdateTransaction(trans.transaction_id, { is_reviewed: !trans.is_reviewed })}
                            className={`p-1 rounded ${trans.is_reviewed ? 'text-emerald-600' : 'text-slate-400'}`}
                          >
                            {trans.is_reviewed ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import View */}
      {activeView === 'import' && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Import Bank Statement</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bank Format
              </label>
              <select
                value={bankFormat}
                onChange={(e) => setBankFormat(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                data-testid="bank-format-select"
              >
                {BANK_FORMATS.map(fmt => (
                  <option key={fmt.value} value={fmt.value}>{fmt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Account Name
              </label>
              <input
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                placeholder="e.g., Business Cheque, Savings"
                data-testid="account-name-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                CSV File
              </label>
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-8 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="hidden"
                  id="csv-upload"
                  data-testid="csv-file-input"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                  <p className="text-slate-600 dark:text-slate-400">
                    {importFile ? importFile.name : 'Click to select CSV file'}
                  </p>
                </label>
              </div>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setActiveView('transactions')}
                className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importing}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="import-csv-btn"
              >
                {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {importing ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules View */}
      {activeView === 'rules' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Categorization Rules</h3>
            <button
              onClick={() => { resetRuleForm(); setEditingRule(null); setShowRuleForm(true); }}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center gap-2"
              data-testid="add-rule-btn"
            >
              <Plus className="w-4 h-4" />
              Add Rule
            </button>
          </div>

          {/* Rule Form Modal */}
          <AnimatePresence>
            {showRuleForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                onClick={() => setShowRuleForm(false)}
              >
                <motion.div
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.95 }}
                  className="bg-white dark:bg-slate-800 rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
                    {editingRule ? 'Edit Rule' : 'Create Rule'}
                  </h3>
                  
                  <form onSubmit={handleSaveRule} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Rule Name</label>
                      <input
                        type="text"
                        value={ruleForm.name}
                        onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                        placeholder="e.g., Telstra Phone Bills"
                        data-testid="rule-name-input"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Conditions</label>
                      {ruleForm.conditions.map((cond, idx) => (
                        <div key={idx} className="flex gap-2 mb-2">
                          <select
                            value={cond.field}
                            onChange={(e) => {
                              const newConds = [...ruleForm.conditions];
                              newConds[idx].field = e.target.value;
                              setRuleForm({ ...ruleForm, conditions: newConds });
                            }}
                            className="px-2 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
                          >
                            <option value="description">Description</option>
                            <option value="amount">Amount</option>
                          </select>
                          <select
                            value={cond.operator}
                            onChange={(e) => {
                              const newConds = [...ruleForm.conditions];
                              newConds[idx].operator = e.target.value;
                              setRuleForm({ ...ruleForm, conditions: newConds });
                            }}
                            className="px-2 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
                          >
                            <option value="contains">Contains</option>
                            <option value="equals">Equals</option>
                            <option value="starts_with">Starts with</option>
                            <option value="greater_than">Greater than</option>
                            <option value="less_than">Less than</option>
                          </select>
                          <input
                            type="text"
                            value={cond.value}
                            onChange={(e) => {
                              const newConds = [...ruleForm.conditions];
                              newConds[idx].value = e.target.value;
                              setRuleForm({ ...ruleForm, conditions: newConds });
                            }}
                            className="flex-1 px-2 py-2 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-sm"
                            placeholder="Value"
                          />
                          {ruleForm.conditions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCondition(idx)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={addCondition}
                        className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Condition
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                        <select
                          value={ruleForm.category}
                          onChange={(e) => setRuleForm({ ...ruleForm, category: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                          data-testid="rule-category-select"
                        >
                          <option value="">Select...</option>
                          {Object.entries(categories).map(([key, cat]) => (
                            <option key={key} value={key}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tax Code</label>
                        <select
                          value={ruleForm.tax_code}
                          onChange={(e) => setRuleForm({ ...ruleForm, tax_code: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                        >
                          <option value="GST">GST</option>
                          <option value="GST-Free">GST-Free</option>
                          <option value="Input Taxed">Input Taxed</option>
                          <option value="N/A">N/A</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowRuleForm(false)}
                        className="px-4 py-2 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
                        data-testid="save-rule-btn"
                      >
                        <Save className="w-4 h-4" />
                        Save Rule
                      </button>
                    </div>
                  </form>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Rules List */}
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
            {rules.length === 0 ? (
              <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                <Settings className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No rules yet. Create rules to auto-categorize transactions.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                {rules.map(rule => (
                  <div key={rule.rule_id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{rule.name}</h4>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          {rule.conditions.map((c, i) => (
                            <span key={i}>
                              {i > 0 && ' AND '}
                              {c.field} {c.operator} "{c.value}"
                            </span>
                          ))}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs rounded">
                            {categories[rule.category]?.name || rule.category}
                          </span>
                          <span className="px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded">
                            {rule.tax_code}
                          </span>
                          <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 text-xs rounded">
                            {rule.times_matched || 0} matches
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditRule(rule)}
                          className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRule(rule.rule_id)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* BAS Export View */}
      {activeView === 'bas' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">BAS Summary Export</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              Generate a Business Activity Statement summary from your reviewed transactions.
            </p>
            
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Start Date</label>
                <input
                  type="date"
                  value={basStartDate}
                  onChange={(e) => setBasStartDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  data-testid="bas-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">End Date</label>
                <input
                  type="date"
                  value={basEndDate}
                  onChange={(e) => setBasEndDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                  data-testid="bas-end-date"
                />
              </div>
              <button
                onClick={handleGetBAS}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 flex items-center gap-2"
                data-testid="generate-bas-btn"
              >
                <BarChart2 className="w-4 h-4" />
                Generate BAS Summary
              </button>
            </div>
          </div>

          {/* BAS Report */}
          {basReport && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  BAS Summary: {basReport.period}
                </h3>
                <span className="text-sm text-slate-500">{basReport.summary.transaction_count} transactions</span>
              </div>

              {/* BAS Fields */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700 dark:text-slate-300">GST on Sales</h4>
                  {['G1', 'G2', 'G3'].map(field => (
                    <div key={field} className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">{field}: {basReport.bas_fields[field].label}</span>
                      <span className="font-medium text-slate-900 dark:text-white">${basReport.bas_fields[field].amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-slate-700 dark:text-slate-300">GST on Purchases</h4>
                  {['G10', 'G11'].map(field => (
                    <div key={field} className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">{field}: {basReport.bas_fields[field].label}</span>
                      <span className="font-medium text-slate-900 dark:text-white">${basReport.bas_fields[field].amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* GST Summary */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">1A: GST on sales</span>
                  <span className="font-medium text-slate-900 dark:text-white">${basReport.bas_fields['1A'].amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">1B: GST on purchases</span>
                  <span className="font-medium text-slate-900 dark:text-white">${basReport.bas_fields['1B'].amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-semibold border-t border-slate-200 dark:border-slate-700 pt-2">
                  <span className="text-slate-900 dark:text-white">9: GST {basReport.bas_fields['9'].amount >= 0 ? 'Payable' : 'Refund'}</span>
                  <span className={basReport.bas_fields['9'].amount >= 0 ? 'text-amber-600' : 'text-emerald-600'}>
                    ${Math.abs(basReport.bas_fields['9'].amount).toLocaleString()}
                  </span>
                </div>
              </div>

              {/* Category Breakdown */}
              <div className="mt-6 border-t border-slate-200 dark:border-slate-700 pt-4">
                <h4 className="font-medium text-slate-700 dark:text-slate-300 mb-3">By Category</h4>
                <div className="space-y-2">
                  {Object.entries(basReport.by_category).slice(0, 10).map(([cat, data]) => (
                    <div key={cat} className="flex justify-between text-sm">
                      <span className="text-slate-600 dark:text-slate-400">{categories[cat]?.name || cat}</span>
                      <span className={`font-medium ${data.total >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        ${data.total.toLocaleString()} ({data.count} trans)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Quick Rules Suggestions View */}
      {activeView === 'suggestions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                Quick Rules Suggestions
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                AI-analyzed patterns from your uncategorized transactions
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={fetchSuggestions}
                disabled={loadingSuggestions}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center gap-2"
                data-testid="refresh-suggestions-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loadingSuggestions ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {suggestions.length > 0 && (
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                  data-testid="accept-all-suggestions-btn"
                >
                  <Zap className="w-4 h-4" />
                  Accept All ({suggestions.length})
                </button>
              )}
            </div>
          </div>

          {loadingSuggestions ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Analyzing transaction patterns...</span>
            </div>
          ) : suggestions.length === 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-8 text-center">
              <Sparkles className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-600 dark:text-slate-400">No patterns found yet.</p>
              <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">Import more transactions to get AI-powered rule suggestions.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {suggestions.map((suggestion, idx) => (
                <motion.div
                  key={suggestion.pattern}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-4"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                          suggestion.confidence === 'high' 
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        }`}>
                          {suggestion.confidence === 'high' ? '✓ High Confidence' : '~ Medium'}
                        </span>
                        <span className="text-xs text-slate-500">{suggestion.occurrences} matches</span>
                      </div>
                      
                      <div className="mb-3">
                        <p className="text-sm text-slate-500 dark:text-slate-400">Pattern detected:</p>
                        <p className="font-mono text-sm font-medium text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded inline-block">
                          "{suggestion.pattern}"
                        </p>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <span className="px-2 py-1 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 text-xs rounded font-medium">
                          → {categories[suggestion.suggested_rule.category]?.name || suggestion.suggested_rule.category}
                        </span>
                        {suggestion.suggested_rule.subcategory && (
                          <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded">
                            {suggestion.suggested_rule.subcategory}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 text-xs rounded">
                          {suggestion.suggested_rule.tax_code}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded ${
                          suggestion.avg_amount >= 0 
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        }`}>
                          Avg: ${Math.abs(suggestion.avg_amount).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleAcceptSuggestion(suggestion)}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                        data-testid={`accept-suggestion-${idx}`}
                      >
                        <Check className="w-4 h-4" />
                        Accept
                      </button>
                      <button
                        onClick={() => setSuggestions(prev => prev.filter(s => s.pattern !== suggestion.pattern))}
                        className="px-4 py-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BankTransactionsTab;
